import { AlertColor, Box, SxProps } from "@mui/material";

import {
  ComponentType,
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
  Fragment
} from "react";

import { Toast, ToastStyle } from "./Toast";

export interface ToastMessage {
  title: string;
  description?: string;
  severity: AlertColor;
  key: number;
  duration?: number;
  isSingleMessage?: boolean;
  withMask?: boolean;
  isExiting?: boolean; //track if toast duration is about to finish
  count?: number; //used to track how many times the same toast has been shown
  trackingLocation?: string; //optional location for analytics
}

const toastContainerStyle: SxProps = {
  pointerEvents: "none",
  "&>*": { pointerEvents: "auto" }
};

const singleMessageStyle: SxProps = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10000
};

export type ToastDisplayedEvent = {
  severity: AlertColor;
  title: string;
  description?: string;
  duplicateCount: number;
  trackingLocation?: string;
};

export type ToastDismissedEvent = {
  severity: AlertColor;
  title: string;
  trackingLocation?: string;
};

export const ToastContext = createContext<{
  addMessage: (message: ToastMessage) => void;
}>(null as never);

export type ToastProviderProps = {
  children: ReactNode;
  onToastDisplayed?: (event: ToastDisplayedEvent) => void;
  onToastDismissed?: (event: ToastDismissedEvent) => void;
  ToastContainerWrapper?: ComponentType<{ children: ReactNode }>;
} & ToastStyle;
/**
 * Provides the context for and renders the Toast component.
 *
 * This should be rendered in the root of the app under a style (or theme) provider that themes the Toast component.
 */
export const ToastProvider: FC<ToastProviderProps> = ({
  children,
  onToastDisplayed,
  onToastDismissed,
  ToastContainerWrapper = Fragment,
  ...props
}) => {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  // setMessages will be stable, memoize the context object so useToast will stabilize
  const context = useMemo(() => {
    return {
      addMessage: (message: ToastMessage) => {
        setMessages((arr) => {
          //Only consider non exiting toasts as "active" for deduping
          const activeToasts = arr.filter((m) => !m.isExiting);
          const possibleDuplicateToast = activeToasts.find(
            (m) => m.title === message.title
          );

          if (possibleDuplicateToast) {
            const newKey = Date.now() + Math.random();
            const newToastArr = arr.filter(
              (m) => possibleDuplicateToast.title !== m.title
            );
            const newCount = (possibleDuplicateToast.count || 0) + 1;
            onToastDisplayed?.({
              severity: message.severity,
              title: message.title,
              description: message.description,
              duplicateCount: newCount,
              trackingLocation: message.trackingLocation
            });

            return [
              ...newToastArr,
              {
                ...message,
                key: newKey,
                count: newCount,
                isExiting: false
              }
            ].slice(-5);
          }

          onToastDisplayed?.({
            severity: message.severity,
            title: message.title,
            description: message.description,
            duplicateCount: 1,
            trackingLocation: message.trackingLocation
          });

          return [
            ...arr,
            {
              ...message,
              count: 1,
              isExiting: false
            }
          ].slice(-5);
        });
      }
    };
  }, [setMessages, onToastDisplayed]);

  const removeMessage = (key: number) =>
    setMessages((arr) => arr.filter((m) => m.key !== key));

  const markAsExiting = (key: number) => {
    setMessages((arr) =>
      arr.map((m) => (m.key === key ? { ...m, isExiting: true } : m))
    );
  };

  return (
    <ToastContext.Provider value={context}>
      {children}
      <ToastContainerWrapper>
        <Box
          position="absolute"
          bottom={2}
          display="flex"
          flexDirection="column"
          width="100%"
          gap={1}
          sx={toastContainerStyle}
        >
          {messages.map((m) => (
            <Fragment key={m.key}>
              <Toast
                message={m}
                onExited={() => removeMessage(m.key)}
                onExiting={() => markAsExiting(m.key)}
                onUserDismissed={() =>
                  onToastDismissed?.({
                    severity: m.severity,
                    title: m.title,
                    trackingLocation: m.trackingLocation
                  })
                }
                duplicateToastCount={m.count || 1}
                {...props}
                sx={m.isSingleMessage ? singleMessageStyle : {}}
              />
              {m.withMask && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    zIndex: 1300
                  }}
                />
              )}
            </Fragment>
          ))}
        </Box>
      </ToastContainerWrapper>
    </ToastContext.Provider>
  );
};

type Options = {
  /** A detailed description of the message */
  description?: string;
  /** Explicitly set the duration of the toast */
  duration?: number;
  /**
   * Darkens the rest of the screen
   * @deprecated Utilize MUI Dialog if a modal style alert is needed
   */
  withMask?: boolean;
  /**
   * shows message in the center of the screen
   * @deprecated
   */
  isSingleMessage?: boolean;
  /** Optional location for analytics tracking */
  trackingLocation?: string;
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw Error(
      "useToast: Context was not found. Seems you forgot to wrap your app in `<ToastProvider />`"
    );
  }

  const { addMessage } = context;

  // since the context is now stable, memoize the return so we don't re-run effects
  return useMemo(() => {
    const show = (
      title: string,
      options: {
        severity: AlertColor;
        withMask?: boolean;
        singleMessage?: boolean;
      },
      description?: string
    ) => {
      addMessage({ title, description, ...options, key: new Date().getTime() });
    };

    return {
      infoToast: (title: string, options?: Options) => {
        show(title, { severity: "info", ...options });
      },
      successToast: (title: string, options?: Options) => {
        show(title, { severity: "success", ...options });
      },
      warningToast: (title: string, options?: Options) => {
        show(title, { severity: "warning", ...options });
      },
      errorToast: (title: string, options?: Options) => {
        show(title, { severity: "error", ...options });
      }
    };
  }, [addMessage]);
};
